import React, { useState, useEffect } from 'react';
import styled from 'styled-components/macro';

import { IconVolumeOn /* IconOpenDictionary */ } from '@mirinae/react-ui';
import { os } from '@mirinae/js-utils/utils/userAgent';
import GTM from '@mirinae/apis/GoogleAnalytics';

import Element from '@mirinae/lessons/components/lesson/elements/Element';
import { playAzureTTS, playTTS } from '@mirinae/lessons/modules/libs/lessonLib';
import { getMetricsForStrings } from '@mirinae/lessons/modules/libs/layoutLib';
import { lessonsSizes, fontDefault, lessonsPalette, zIndex } from '@mirinae/lessons/modules/defines/styles';

import { LessonButton } from '@mirinae/lessons/modules/defines/styles';
import { HelpBubble, registerHelp } from '@mirinae/lessons/components/lesson/Help';
import { azureVoicings } from '@mirinae/lessons/components/lesson/elements/LetterCard';

const VocabCardElement = styled(Element)`
    display: flex;
    flex-direction: row;
    justify-content: center;;
    ${!os.isOthers && 'min-width: 100%;'}
`;

const CardWrapper = styled.div`
    display: flex;
    flex-direction: row;
    position: relative;
    height: ${os.isOthers ? '100%' : 'calc(100% - 8px)'};
    width: ${props => (os.isOthers ? '100%' : `${props.geom.width}px`)};
    ${props => os.isOthers && `min-width: ${props.geom.width}px`};
    ${!os.isOthers && 'max-width: calc(100vw - 100px);'} // room for the side stpe-controls in mobile on narrow phones 
`;

const CardTop = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: calc(100% - 52px);
    background:  ${lessonsPalette.primary.white};
    border: 1px solid ${lessonsPalette.grey[6]};
    box-sizing: border-box;
    box-shadow: 4px 4px 13px rgba(0, 0, 0, 0.07);
    border-radius: 10px;
    cursor: pointer;
    z-index: ${zIndex.float};
`;

const MeaningDrawer = styled.div`
    position: absolute;
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 96%;
    height: calc(100% - 50px);
    background: ${lessonsPalette.light[0]};
    top: 5px;
    left: 3%;
    border: 1px solid ${lessonsPalette.grey[5]};
    box-shadow: 4px 4px 13px rgba(0, 0, 0, 0.07);
    border-radius: 10px;
    ${props => props.state !== 'closed' && `
        animation: 0.25s ease-in-out forwards ${props.state}Drawer;
    `};

    @keyframes teaseDrawer { 
        0% { top: 5px; background: ${lessonsPalette.light[0]} } 
        100% { top: 7px; background: ${lessonsPalette.default[0]} }
    };
    @keyframes unteaseDrawer { 
        0% { top: 7px; background: ${lessonsPalette.default[0]}  }
        100% { top: 5px; background: ${lessonsPalette.light[0]} }
    };
    @keyframes openDrawer { 
        0% { top: 7px; background: ${lessonsPalette.default[0]}  } 
        100% { top: 55px; background: ${lessonsPalette.default[0]}  }
    };
    @keyframes backToTeaseDrawer { 
        0% { top: 55px; background: ${lessonsPalette.default[0]}  }
        100% { top: 7px; background: ${lessonsPalette.default[0]}  }
    };
`;

const Word = styled.div`
    font-family: Noto Sans KR;
    font-style: normal;
    font-weight: normal;
    font-size: 28px;
    line-height: 35px;
    display: flex;
    text-align: center;
    color: ${lessonsPalette.grey[0]};
    ${!os.isOthers && `
        font-size: 38px;
    `}
`;

const Pronunciation = styled.div`
    position: absolute;
    bottom: 72px;
    font-family: ${fontDefault};
    font-style: normal;
    font-weight: normal;
    font-size: 12px;
    line-height: 15px;
    color: ${lessonsPalette.grey[3]};
`;

const ButtonStyle = `
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    top: 16px;
    width: 35px;
    height: 35px;
    border-radius: 10px;
    border: 1px solid ${lessonsPalette.grey[5]};
    &:hover:not(:disabled) {
        background-color: ${lessonsPalette.default[0]};
        border: 0;
        svg path {
            fill: ${lessonsPalette.primary.white};
        }
    }
    &:disabled {
        background-color: ${lessonsPalette.grey[5]};
        border: 0;
        svg path {
            fill: ${lessonsPalette.primary.white};
        }
    }
`;
const PlayAudioButton = styled(LessonButton)`
    left: 15px;
    ${ButtonStyle}
`;

// const OpenDictionaryButton = styled(LessonButton)`
//     right: 15px;
//     ${ButtonStyle}
//     svg {
//         margin-left: 2.5px;
//         margin-bottom: 2px;
//     }
// `;

const MeaningText = styled.div`
    position: absolute;
    bottom: 9px;
    font-style: normal;
    font-weight: 600;
    font-size: ${props => parseInt(props.scale * 20, 10)}px;
    text-align: center;
    padding: 0px 10px;
    color: ${lessonsPalette.primary.white};
    word-wrap: break-word;
    word-break: keep-all;
    display: flex;
    height: 40px;
    align-items: center;
`;

const smallestScale = 0.65;

const Meaning = ({ text, maxWidth }) => {
    const [scale, setScale] = useState(1);

    useEffect(() => {
        const metrics = getMetricsForStrings([text], 'lesson-english-phrase', lessonsSizes.vocabMeaningFontSize)[0];
        setScale(metrics.width > maxWidth ? Math.max(smallestScale, maxWidth / metrics.width) : 1);
    }, []);
    return (
        <MeaningText scale={scale}>{text}</MeaningText>
    );
};

const VocabCard = ({ element, geomElement }) => {
    const [state, setState] = useState('closed');
    const [audioEl, setAudioEl] = useState(null);

    const enter = () => {
        setState(state !== 'open' ? 'tease' : 'open');
    };

    const leave = () => {
        setState(state.match(/tease|backToTease/) ? 'untease' : state);
    };

    const click = () => {
        setState(state === 'open' ? 'backToTease' : 'open');
        if (state !== 'open')
            GTM.userAction('vocab-meaning', { word: element.fieldsByName.word.value }, 'lessons');
    };

    const playAudio = (e) => {
        // playTTS(null, element.fieldsByName.word.value, e);
        playAzureTTS(audioEl, element.fieldsByName.word.value, e, azureVoicings['Female normal']);
        GTM.userAction('vocab-audio', { word: element.fieldsByName.word.value }, 'lessons');
    };

    return (
        <VocabCardElement className={`el-${element.componentName}`} element={element} geomElement={geomElement}>
            <CardWrapper geom={os.isOthers ? geomElement.geom : (geomElement.mobile && geomElement.mobile.geom)}>
                <CardTop className="vocab-card" onMouseEnter={enter} onMouseLeave={leave} onClick={click}>
                    <PlayAudioButton type="button" onClick={playAudio}><IconVolumeOn /></PlayAudioButton>
                    { /* state === 'open' && <OpenDictionaryButton><IconOpenDictionary /></OpenDictionaryButton>  disabled for now, please implement!! */}
                    <Word>{element.fieldsByName.word.value}</Word>
                    <Pronunciation>{element.fieldsByName.pronunciation.value}</Pronunciation>
                </CardTop>
                <MeaningDrawer state={state}>
                    <Meaning text={element.fieldsByName.english.value} maxWidth={180} />
                </MeaningDrawer>
            </CardWrapper>
            <audio ref={setAudioEl} />
        </VocabCardElement>
    );
};

registerHelp(VocabCard, [
    <HelpBubble
        title="Click card to see meaning"
        desktop={{ titleAt: 'bottom', arrowPos: 'left', arrowDir: 'down', style: { bottom: 353, left: 407, width: 230 } }}
        mobile={{ anchor: '.vocab-card', titleAt: 'bottom', arrowPos: 'right', arrowDir: 'down', style: { bottom: 265, right : 40, width: 230 } }}>
        <p>Each card displays a word or used in this lesson, along with a pronunciation guide at the bottom</p>
        <p>If you need to check, click on a card to reveal the meaning.</p>
    </HelpBubble>,
    <HelpBubble
        title="Click to hear pronunciation"
        desktop={{ titleAt: 'top', arrowPos: 'right', arrowDir: 'right', style: { top: 183, left: 102, width: 230 } }}
        mobile={{ anchor: '.vocab-card', titleAt: 'top', arrowPos: 'left', arrowDir: 'up', style: { top: 58, left: 24, width: 230 } }}>
        Hear the word or phrase pronounced by clicking the speaker icon.
    </HelpBubble>,
]);

export default VocabCard;
